// SG-1394: port of searchIdAppender.js that adds sid, search_kind, search_surface and search_version to result links.
// Done in the browser: the cards are fragment-cached, so a server-rendered search id would reach other visitors.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CLICK_PATH = /\/clicks\/track\/?$/;
const STORE_PATH = /^\/stores\/[^/]+\/?$/;
const LINK_ATTRIBUTES = ["href", "card-href", "action-href"];
const ATTRIBUTION_PARAMS = [
  "sid",
  "search_kind",
  "search_surface",
  "search_version",
];

function attributionFromResultsPage(doc, loc) {
  const el = doc.querySelector("[data-search-id]");
  // No term, no search: browsing /offers or /stores must not inherit an earlier search's id.
  if (!el || !new URLSearchParams(loc.search).get("q")) return null;

  return {
    sid: el.dataset.searchId,
    search_kind: el.dataset.searchKind,
    search_surface: "results",
    search_version: el.dataset.searchVersion,
  };
}

function attributionFromUrl(loc) {
  const params = new URLSearchParams(loc.search);
  return {
    sid: params.get("sid"),
    search_kind: params.get("search_kind"),
    search_surface: params.get("search_surface"),
    search_version: params.get("search_version"),
  };
}

function tagged(value, attribution, pathPatterns, loc) {
  let url;
  try {
    url = new URL(value, loc.href);
  } catch {
    return null;
  }

  // Never add our ids to a partner URL.
  if (url.origin !== loc.origin) return null;
  if (!pathPatterns.some((pattern) => pattern.test(url.pathname))) return null;
  if (url.searchParams.has("sid")) return null;

  ATTRIBUTION_PARAMS.forEach((name) => {
    if (attribution[name]) url.searchParams.set(name, attribution[name]);
  });

  return value.startsWith("/")
    ? `${url.pathname}${url.search}${url.hash}`
    : url.toString();
}

export function tagSearchLinks(doc = document, loc = window.location) {
  const onResults = attributionFromResultsPage(doc, loc);
  const attribution = onResults || attributionFromUrl(loc);
  if (!attribution.sid || !UUID.test(attribution.sid)) return;

  const pathPatterns = onResults ? [CLICK_PATH, STORE_PATH] : [CLICK_PATH];
  const selector = LINK_ATTRIBUTES.map((name) => `[${name}]`).join(",");

  // The header, ribbon and footer sit inside <main> too, so scope to the results themselves
  // (or, on a store page, the page content) rather than to <main>.
  const scope = onResults ? "#search-results" : "#main-content";

  doc.querySelectorAll(selector).forEach((el) => {
    try {
      if (!el.closest(scope)) return;

      LINK_ATTRIBUTES.forEach((name) => {
        const value = el.getAttribute(name);
        if (!value) return;

        const next = tagged(value, attribution, pathPatterns, loc);
        if (next) el.setAttribute(name, next);
      });
    } catch (error) {
      console.error("search_attribution: could not tag a link", error);
    }
  });
}

// Best-effort: tagging must never break the page or the scripts bundled after this one.
export function tagSearchLinksSafely(doc = document, loc = window.location) {
  try {
    tagSearchLinks(doc, loc);
  } catch (error) {
    console.error("search_attribution: tagging failed", error);
  }
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => tagSearchLinksSafely());
  } else {
    tagSearchLinksSafely();
  }
}
